import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import CommonButton from "../../Components/InputComponents/CommonButton";
import { useStaffLayout } from "../../Components/Context/CandidateLayoutContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const JobListing = ({ isAdmin }) => {
  const { privilegeData } = useStaffLayout();

  function hasJobAddprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName.toLowerCase().includes("job");

      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "add" && priv.assigned == true
      );
    });
  }

  function hasJobEditprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName.toLowerCase().includes("job");
      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned == true
      );
    });
  }

  function hasJobViewprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName.toLowerCase().includes("job");
      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "view" && priv.assigned == true
      );
    });
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const [jobData, setJobData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageCount, setPageCount] = useState("");
  const [loaderOpen, setLoaderOpen] = useState(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
  };

  const [filterData, setFilterData] = useState({
    title: "",
    country: "",
    qualification: "",
    profession: "",
    jobID: "",
    status_id: 1,
  });

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
  };

  const handleResetFilters = () => {
    const resetFilters = {
      title: "",
      country: "",
      qualification: "",
      profession: "",
      jobID: "",
      status_id: 1,
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }

    navigate(`/Jobs`, { replace: true });
  };

  const navigatetoAdd = () => {
    navigate("/Jobs/Add", { state: paginationState });
  };
  const navigatetoEdit = (id) => {
    const encrypted = encodeURIComponent(encryptId(id?.JD_Id));
    navigate(`/Jobs/Edit/${encrypted}`, {
      state: {
        ...paginationState,
        totalApplicants: id?.Total_Applicants,
      },
    });
  };
  const navigatetoView = (id) => {
    const encrypted = encodeURIComponent(encryptId(id?.JD_Id));
    navigate(`/Jobs/View/${encrypted}`, { state: paginationState });
  };

  const navigateToApplicantList = (id, jobTitle) => {
    const encrypted = encodeURIComponent(encryptId(id?.JD_Id));
    const encryptedTitle = encodeURIComponent(encryptId(jobTitle));
    navigate(`/Jobs/Applicants/${encrypted}/${encryptedTitle}`, {
      state: paginationState,
    });
  };

  const [countryList, setCountryList] = useState([]);
  const [qualificationList, setQualificationList] = useState([]);
  const [professionList, setProfessionList] = useState([]);

  const getCountries = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentCountry/list`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getQualification = async () => {
    try {
      const res = await axiosAPI.get(`/Qualification/list`);
      setQualificationList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = (filters = filterData) => {
    const payload = {
      job_id:
        typeof filters?.jobID === "string"
          ? filters.jobID.trim()
          : filters?.jobID,
      title:
        typeof filters?.title === "string"
          ? filters.title.trim()
          : filters?.title,
      client_id: "",
      branch_id: "",
      country_id:
        typeof filters?.country === "string"
          ? filters.country.trim()
          : filters?.country,
      profession_id:
        typeof filters?.profession === "string"
          ? filters.profession.trim()
          : filters?.profession,
      qualification_id:
        typeof filters?.qualification === "string"
          ? filters.qualification.trim()
          : filters?.qualification,
      start_date: "",
      end_date: "",
      status_id: filters?.status_id,
    };

    axiosAPI
      ?.post(`/Job/${rowsPerPage}/${page + 1}/list`, payload)

      .then((res) => {
        setPageCount(res?.data?.data?.total_count);
        setJobData(res?.data?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    getCountries();
    getQualification();
    getProfession();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (location?.state?.pagination) {
      setPage(location.state.pagination.page || 0);
      setRowsPerPage(location.state.pagination.rowsPerPage || 25);
    }

    if (location?.state?.filterData) {
      setFilterData(location.state.filterData);
      getData(location.state.filterData); // fetch with restored filters
    } else {
      getData();
    }
  }, [location?.state, page, rowsPerPage]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Job Management
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {hasJobAddprivilege(privilegeData) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ background: "#E75E1F" }}
              onClick={navigatetoAdd}
              disabled={!hasJobAddprivilege(privilegeData)}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ maxWidth: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job ID
            </Typography>
            <TextField
              placeholder="Job ID"
              variant="outlined"
              size="small"
              value={filterData.jobID}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, jobID: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job Title
            </Typography>
            <TextField
              placeholder="Job Title"
              variant="outlined"
              size="small"
              value={filterData.title}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, title: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Profession
            </Typography>
            <SelectInput
              value={filterData?.profession}
              name="profession"
              onChange={handleFilterChange}
              options={professionList?.map((item) => ({
                value: item?.PI_Id,
                label: item?.PI_Name,
              }))}
              placeholder="Select Profession"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Country
            </Typography>
            <SelectInput
              value={filterData?.country}
              name="country"
              onChange={handleFilterChange}
              options={countryList?.map((item) => ({
                value: item.RC_Id,
                label: item.RC_CountryName,
              }))}
              placeholder="Select Country"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Qualification
            </Typography>
            <SelectInput
              value={filterData?.qualification}
              name="qualification"
              onChange={handleFilterChange}
              options={qualificationList?.map((item) => ({
                value: item?.QL_Id,
                label: item?.QL_Name,
              }))}
              placeholder="Select Qualification"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {open && (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Status
              </Typography>
              <SelectInput
                value={filterData?.status_id}
                name="status_id"
                onChange={(e) =>
                  setFilterData((prev) => ({
                    ...prev,
                    status_id: e.target.value,
                  }))
                }
                options={[
                  { value: "", label: "All" },
                  { value: 1, label: "Open" },
                  { value: 2, label: "Closed" },
                ]}
                placeholder="Select Status"
                sx={{ width: { xs: "100%" } }}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setOpen(!open)}
          sx={{ textTransform: "capitalize" }}
        >
          {open ? "Less" : "More"} Filters
        </Button>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "100%",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell sx={{ minWidth: "150px" }}>
                {" "}
                Job ID{" "}
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: "220px" }}>
                Job Title
              </StyledTableCell>
              <StyledTableCell>Profession</StyledTableCell>
              <StyledTableCell>Country</StyledTableCell>
              <StyledTableCell>Qualification</StyledTableCell>
              <StyledTableCell sx={{ minWidth: "150px" }}>
                Client
              </StyledTableCell>
              <StyledTableCell>Recruitment Procedure</StyledTableCell>
              {hasJobViewprivilege(privilegeData) && (
                <StyledTableCell align="center">
                  Total Applicants
                </StyledTableCell>
              )}
              <StyledTableCell align="center">Status</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "150px" }}>
                {" "}
                Actions{" "}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 11 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))
            ) : jobData && jobData.length > 0 ? (
              jobData.map((parent, index) => (
                <StyledTableRow key={parent.id}>
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {index + 1 + page * rowsPerPage}
                  </StyledTableCell>

                  <StyledTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      {parent.JD_JobId}
                    </Typography>
                  </StyledTableCell>

                  <StyledTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      {parent.JD_Title ? parent.JD_Title : "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      {parent.JD_Profession ? parent.JD_Profession : "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent.JD_Country ? parent.JD_Country : "-"}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {Array.isArray(parent.JD_Qualification)
                      ? parent.JD_Qualification.map((a) => a?.Name).join(", ")
                      : parent.JD_Qualification || "-"}
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent?.JD_Client ? parent?.JD_Client : "-"}
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent.JD_Recuritmernt_procedure_name
                      ? parent.JD_Recuritmernt_procedure_name
                      : "-"}
                  </StyledTableCell>

                  {hasJobViewprivilege(privilegeData) && (
                    <StyledTableCell
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        textAlign: "center",
                      }}
                      onClick={() => {
                        if (parent.Total_Applicants) {
                          navigateToApplicantList(parent, parent?.JD_Title);
                        }
                      }}
                    >
                      {parent.Total_Applicants ? (
                        <Chip
                          component="button"
                          sx={{
                            border: "2px solid grey",
                            cursor: "pointer",
                            padding: "2px 10px",
                            transition: "all 0.2s ease",
                            backgroundColor: "#f5f5f5",
                            borderColor: "#006CB8",
                            fontWeight: 500,
                            "&:hover": {
                              background: "white",
                              color: "#1976d2",
                            },
                          }}
                          label={parent.Total_Applicants}
                        />
                      ) : (
                        <Chip
                          component="button"
                          label="0"
                          sx={{
                            border: "2px solid grey",
                            cursor: "not-allowed",
                            padding: "2px 15px",
                            color: "grey",
                            backgroundColor: "#f0f0f0",
                          }}
                          disabled
                        />
                      )}
                    </StyledTableCell>
                  )}

                  <StyledTableCell sx={{ width: "150px", textAlign: "right" }}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Chip
                        label={parent.JD_Status == 1 ? "Open" : "Closed"}
                        color={parent.JD_Status == "1" ? "success" : "error"}
                        sx={{
                          fontSize: { xs: "10px", sm: "12px" },
                          width: "80px",
                          fontFamily: "Geist, sans-serif",
                          fontWeight: "600",
                        }}
                      />
                    </Box>
                  </StyledTableCell>
                  {(hasJobViewprivilege(privilegeData) ||
                    hasJobEditprivilege(privilegeData)) && (
                    <StyledTableCell align="center">
                      {hasJobViewprivilege(privilegeData) && (
                        <IconButton
                          color="primary"
                          onClick={() => navigatetoView(parent)}
                          disabled={!hasJobViewprivilege(privilegeData)}
                        >
                          <Visibility />
                        </IconButton>
                      )}
                      {hasJobEditprivilege(privilegeData) && (
                        <IconButton
                          color="warning"
                          onClick={() => navigatetoEdit(parent)}
                          disabled={!hasJobEditprivilege(privilegeData)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={11} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={pageCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />

      {/* <Backdrop
        open={loaderOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop> */}
    </Paper>
  );
};

export default JobListing;
